import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLocation } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { updateFeedbackMessage } from "@/lib/feedback.functions";
import { readExploreActivity } from "@/lib/explore-activity";

// ─────────────────────────────────────────────────────────────────────────────
// Shared feedback form — used by the floating bubble, the footer Contact link,
// and the Plan-page rating flow. When `feedbackId` is provided, we UPDATE that
// existing rating-only row server-side; otherwise we INSERT a new row directly.
// ─────────────────────────────────────────────────────────────────────────────

const HEADLINE = "Help us improve RanchPlanner";
const SUBHEAD =
  "Found a bug, broken link, or wrong info? Have a suggestion, missing event/venue, or just want to say hi? Drop a note — we read every one.";

interface FeedbackFormProps {
  pageUrl?: string | null;
  feedbackId?: string | null;
  rating?: "yes" | "no" | null;
  onSubmitted?: () => void;
  compact?: boolean;
}

export function FeedbackForm({ pageUrl, feedbackId, rating, onSubmitted, compact }: FeedbackFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: "", message: "" });
  const updateFeedbackMessageFn = useServerFn(updateFeedbackMessage);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.message.trim()) return;
    setSubmitting(true);
    try {
      if (feedbackId) {
        // Update an existing rating-only row
        const result = await updateFeedbackMessageFn({
          data: {
            feedbackId,
            message: form.message.trim(),
            email: form.email.trim() || undefined,
          },
        });
        if (!result.ok) {
          toast.error(result.error || "Could not save feedback. Please try again.");
          setSubmitting(false);
          return;
        }
      } else {
        const { error } = await supabase.from("site_feedback").insert({
          email: form.email.trim() || null,
          message: form.message.trim(),
          page_url: pageUrl ?? (typeof window !== "undefined" ? window.location.pathname : null),
          rating: rating ?? null,
        });
        if (error) {
          toast.error("Failed to send. Please try again.");
          setSubmitting(false);
          return;
        }
      }
      toast.success("Thanks for your feedback! We appreciate it. 🧡", { duration: 4000 });
      setForm({ email: "", message: "" });
      setSubmitting(false);
      onSubmitted?.();
    } catch {
      toast.error("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {!compact && (
        <div>
          <p className="text-sm font-bold text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>
            {HEADLINE}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{SUBHEAD}</p>
        </div>
      )}
      <label className="block">
        <span className="sr-only">Your feedback</span>
        <textarea
          placeholder="What's on your mind?"
          aria-label="Your feedback"
          value={form.message}
          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          required
          maxLength={1000}
          rows={4}
          className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
        />
      </label>
      <label className="block">
        <span className="sr-only">Email (optional, for follow-up)</span>
        <input
          type="email"
          placeholder="Email (optional, for follow-up)"
          aria-label="Email (optional, for follow-up)"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          maxLength={255}
          className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </label>
      <button
        type="submit"
        disabled={submitting || !form.message.trim()}
        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        <Send className="h-3.5 w-3.5" />
        {submitting ? "Sending..." : "Send"}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Centered modal wrapper around FeedbackForm
// ─────────────────────────────────────────────────────────────────────────────

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  pageUrl?: string | null;
  feedbackId?: string | null;
  rating?: "yes" | "no" | null;
  title?: string;
  subtitle?: string;
}

export function FeedbackModal({ open, onClose, pageUrl, feedbackId, rating, title, subtitle }: FeedbackModalProps) {
  // ESC key closes the modal (WCAG 2.1.1 keyboard).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm print:hidden"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
            aria-describedby="feedback-modal-subtitle"
          >
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <p id="feedback-modal-title" className="text-base font-bold text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {title ?? HEADLINE}
                </p>
                <p id="feedback-modal-subtitle" className="text-xs text-muted-foreground mt-1">{subtitle ?? SUBHEAD}</p>
              </div>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" aria-label="Close feedback dialog">
                <X className="h-4 w-4" />
              </button>
            </div>
            <FeedbackForm
              pageUrl={pageUrl}
              feedbackId={feedbackId}
              rating={rating}
              compact
              onSubmitted={onClose}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Floating bubble — only on /explore, only after meaningful activity.
// Activity helpers live in @/lib/explore-activity so this file stays
// component-only (required for Vite React Fast Refresh).
// ─────────────────────────────────────────────────────────────────────────────

export function SiteFeedback() {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isResultsPage = location.pathname === "/explore";

  useEffect(() => {
    if (!isResultsPage) {
      setShow(false);
      setOpen(false);
      return;
    }

    let armed = false;
    const arm = () => {
      if (armed) return;
      armed = true;
      setShow(true);
    };

    const checkActivity = () => {
      const { opens, saves } = readExploreActivity();
      if (opens >= 3 || saves >= 1) arm();
    };
    checkActivity();

    // Fallback: 90s on the page
    const timer = window.setTimeout(arm, 90_000);

    // Scroll past ~50%
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = Math.max(doc.scrollHeight, 1);
      if (scrolled / total > 0.5) arm();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("rp-activity", checkActivity);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("rp-activity", checkActivity);
    };
  }, [isResultsPage]);

  if (!show || !isResultsPage) return null;

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 left-6 z-40 p-3 rounded-full bg-muted/90 backdrop-blur-md border border-border shadow-lg hover:shadow-xl transition-all hover:scale-105 text-muted-foreground hover:text-foreground print:hidden"
            aria-label="Send feedback"
          >
            <MessageSquare className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-6 z-50 w-80 rounded-2xl border border-border bg-card shadow-2xl p-5 print:hidden"
            role="dialog"
            aria-modal="false"
            aria-label="Send feedback"
          >
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <p className="text-sm font-bold text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {HEADLINE}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{SUBHEAD}</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close feedback" className="text-muted-foreground hover:text-foreground transition-colors shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>
            <FeedbackForm
              pageUrl="/explore"
              compact
              onSubmitted={() => {
                setOpen(false);
                setShow(false);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
